/**
 * ImportContactsPage — /app/contatos/importar
 *
 * Fluxo em 4 passos:
 *  1. Upload — drag-and-drop de CSV ou XLSX
 *  2. Mapeamento — de → para entre colunas do arquivo e campos do sistema
 *  3. Prévia — amostra de 10 linhas + contagem de erros de validação
 *  4. Resultado — resumo pós-importação (inserted / updated / errors)
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Upload, FileSpreadsheet, ArrowRight, ArrowLeft,
  CheckCircle2, AlertTriangle, XCircle, Loader2,
  Users, RefreshCw, Info, ChevronDown,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';

// ── Campos aceitos pelo sistema ───────────────────────────────────────────────

interface FieldDef {
  key:      string;
  label:    string;
  required: boolean;
  hint?:    string;
}

const SYSTEM_FIELDS: FieldDef[] = [
  { key: 'full_name',          label: 'Nome completo',      required: true  },
  { key: 'category',           label: 'Categoria',          required: false, hint: 'eleitor, lideranca, voluntario, doador, simpatizante' },
  { key: 'phone',              label: 'Telefone',           required: false, hint: 'com ou sem DDI/DDD' },
  { key: 'phone_whatsapp',     label: 'Aceita WhatsApp',    required: false, hint: 'sim/não, true/false, 1/0' },
  { key: 'email',              label: 'E-mail',             required: false },
  { key: 'birth_date',         label: 'Data de nascimento', required: false, hint: 'DD/MM/AAAA ou AAAA-MM-DD' },
  { key: 'gender',             label: 'Gênero',             required: false, hint: 'M, F, outro' },
  { key: 'cpf',                label: 'CPF',                required: false },
  { key: 'neighborhood',       label: 'Bairro',             required: false },
  { key: 'city',               label: 'Cidade',             required: false },
  { key: 'state_code',         label: 'UF',                 required: false, hint: 'ex: AL, SP, RJ' },
  { key: 'zip_code',           label: 'CEP',                required: false },
  { key: 'street',             label: 'Rua / Logradouro',   required: false },
  { key: 'street_number',      label: 'Número',             required: false },
  { key: 'voter_registration', label: 'Título de eleitor',  required: false },
  { key: 'electoral_zone',     label: 'Zona eleitoral',     required: false },
  { key: 'electoral_section',  label: 'Seção eleitoral',    required: false },
  { key: 'engagement_score',   label: 'Engajamento (0–100)',required: false },
  { key: 'latitude',           label: 'Latitude',           required: false },
  { key: 'longitude',          label: 'Longitude',          required: false },
  { key: 'notes',              label: 'Observações',        required: false },
  { key: 'tags',               label: 'Tags',               required: false, hint: 'separadas por vírgula' },
];

const IGNORE_KEY = '__ignore__';

// ── Tipos internos ─────────────────────────────────────────────────────────────

type RawRow   = Record<string, string>;
type Step     = 'upload' | 'mapping' | 'preview' | 'result';

interface ValidatedRow {
  _rowIndex: number;
  _errors:   string[];
  full_name:          string;
  category:           string;
  phone?:             string | null;
  phone_whatsapp?:    boolean;
  email?:             string | null;
  birth_date?:        string | null;
  gender?:            string | null;
  cpf?:               string | null;
  neighborhood?:      string | null;
  city?:              string | null;
  state_code?:        string | null;
  zip_code?:          string | null;
  street?:            string | null;
  street_number?:     string | null;
  voter_registration?: string | null;
  electoral_zone?:    string | null;
  electoral_section?: string | null;
  engagement_score?:  number;
  latitude?:          number | null;
  longitude?:         number | null;
  notes?:             string | null;
  tags?:              string[];
}

interface ImportResult {
  inserted: number;
  updated:  number;
  skipped:  number;
  errors:   Array<{ row: number; message: string }>;
}

// ── Helpers de parse ───────────────────────────────────────────────────────────

function parseBool(v: string | undefined): boolean {
  if (!v) return true;
  const l = v.trim().toLowerCase();
  return ['true', 'sim', 's', '1', 'yes'].includes(l);
}

function parseDate(v: string | undefined): string | null {
  if (!v?.trim()) return null;
  // AAAA-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return v.trim();
  // DD/MM/AAAA
  const m = v.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return null;
}

function parseFloat2(v: string | undefined): number | null {
  if (!v?.trim()) return null;
  const n = parseFloat(v.replace(',', '.'));
  return isNaN(n) ? null : n;
}

function validateRow(raw: RawRow, mapping: Record<string, string>, idx: number): ValidatedRow {
  const get = (key: string): string | undefined => {
    const col = Object.entries(mapping).find(([, v]) => v === key)?.[0];
    return col ? (raw[col] ?? '').trim() : undefined;
  };

  const errors: string[] = [];

  const full_name = get('full_name') ?? '';
  if (!full_name) errors.push('Nome obrigatório');

  const engRaw = get('engagement_score');
  const engNum = engRaw ? parseInt(engRaw, 10) : 0;

  return {
    _rowIndex:          idx,
    _errors:            errors,
    full_name,
    category:           get('category')?.toLowerCase() || 'eleitor',
    phone:              get('phone') || null,
    phone_whatsapp:     parseBool(get('phone_whatsapp')),
    email:              get('email') || null,
    birth_date:         parseDate(get('birth_date')),
    gender:             get('gender') || null,
    cpf:                get('cpf') || null,
    neighborhood:       get('neighborhood') || null,
    city:               get('city') || null,
    state_code:         get('state_code')?.toUpperCase() || null,
    zip_code:           get('zip_code') || null,
    street:             get('street') || null,
    street_number:      get('street_number') || null,
    voter_registration: get('voter_registration') || null,
    electoral_zone:     get('electoral_zone') || null,
    electoral_section:  get('electoral_section') || null,
    engagement_score:   isNaN(engNum) ? 0 : Math.min(100, Math.max(0, engNum)),
    latitude:           parseFloat2(get('latitude')),
    longitude:          parseFloat2(get('longitude')),
    notes:              get('notes') || null,
    tags:               get('tags')
                          ? (get('tags') as string).split(/[,;]/).map((t) => t.trim().toLowerCase()).filter(Boolean)
                          : [],
  };
}

// ── Componente principal ───────────────────────────────────────────────────────

export function ImportContactsPage() {
  const navigate    = useNavigate();
  const qc          = useQueryClient();
  const dropRef     = useRef<HTMLDivElement>(null);

  const [step,         setStep]        = useState<Step>('upload');
  const [fileName,     setFileName]    = useState('');
  const [rawRows,      setRawRows]     = useState<RawRow[]>([]);
  const [columns,      setColumns]     = useState<string[]>([]);
  const [mapping,      setMapping]     = useState<Record<string, string>>({});  // col → fieldKey
  const [validated,    setValidated]   = useState<ValidatedRow[]>([]);
  const [importing,    setImporting]   = useState(false);
  const [result,       setResult]      = useState<ImportResult | null>(null);
  const [dragOver,     setDragOver]    = useState(false);

  // ── Parse do arquivo ─────────────────────────────────────────────────────

  const parseFile = useCallback((file: File) => {
    setFileName(file.name);

    if (file.name.endsWith('.csv') || file.type === 'text/csv') {
      Papa.parse<RawRow>(file, {
        header:         true,
        skipEmptyLines: true,
        complete: (res) => {
          setRawRows(res.data);
          setColumns(res.meta.fields ?? []);
          autoMap(res.meta.fields ?? []);
          setStep('mapping');
        },
      });
    } else {
      // XLSX / XLS / ODS
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb   = XLSX.read(e.target?.result, { type: 'binary' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json<RawRow>(ws, { defval: '' });
        const cols = data.length > 0 ? Object.keys(data[0]) : [];
        setRawRows(data);
        setColumns(cols);
        autoMap(cols);
        setStep('mapping');
      };
      reader.readAsBinaryString(file);
    }
  }, []); // eslint-disable-line

  /** Tenta mapear automaticamente por nome de coluna */
  const autoMap = (cols: string[]) => {
    const m: Record<string, string> = {};
    const aliases: Record<string, string[]> = {
      full_name:          ['nome', 'name', 'nome completo', 'full_name', 'full name'],
      phone:              ['telefone', 'celular', 'phone', 'fone', 'tel'],
      email:              ['email', 'e-mail', 'correio'],
      city:               ['cidade', 'city', 'municipio', 'município'],
      state_code:         ['uf', 'estado', 'state', 'state_code'],
      neighborhood:       ['bairro', 'neighborhood'],
      category:           ['categoria', 'category', 'perfil'],
      birth_date:         ['nascimento', 'data nascimento', 'birth_date', 'birthday', 'data de nascimento'],
      cpf:                ['cpf'],
      zip_code:           ['cep', 'zip', 'zip_code'],
      street:             ['rua', 'logradouro', 'endereco', 'endereço', 'street'],
      street_number:      ['numero', 'número', 'number', 'street_number'],
      engagement_score:   ['engajamento', 'engagement', 'score', 'pontuacao', 'pontuação'],
      latitude:           ['latitude', 'lat'],
      longitude:          ['longitude', 'lng', 'lon'],
      notes:              ['observacoes', 'observações', 'notas', 'notes', 'obs'],
      tags:               ['tags', 'etiquetas'],
      voter_registration: ['titulo', 'título', 'titulo eleitor', 'voter_registration'],
      electoral_zone:     ['zona', 'zona eleitoral', 'electoral_zone'],
      electoral_section:  ['secao', 'seção', 'secao eleitoral', 'electoral_section'],
      phone_whatsapp:     ['whatsapp', 'phone_whatsapp', 'aceita whatsapp'],
      gender:             ['genero', 'gênero', 'sexo', 'gender'],
    };
    cols.forEach((col) => {
      const lower = col.toLowerCase().trim();
      for (const [field, names] of Object.entries(aliases)) {
        if (names.some((n) => lower.includes(n) || n.includes(lower))) {
          m[col] = field;
          return;
        }
      }
      m[col] = IGNORE_KEY;
    });
    setMapping(m);
  };

  // ── Drag-and-drop ─────────────────────────────────────────────────────────

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  // ── Ir para prévia (valida todas as linhas) ───────────────────────────────

  const goToPreview = () => {
    const rows = rawRows.map((r, i) => validateRow(r, mapping, i + 1));
    setValidated(rows);
    setStep('preview');
  };

  // ── Importar ──────────────────────────────────────────────────────────────

  const runImport = async () => {
    setImporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const clean = validated
        .filter((r) => r._errors.length === 0)
        .map(({ _rowIndex: _i, _errors: _e, ...rest }) => rest);

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-contacts`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ rows: clean }),
        },
      );

      const data: ImportResult = await res.json();
      setResult(data);
      setStep('result');
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['geo-stats'] });
      void qc.invalidateQueries({ queryKey: ['geo-summary'] });
    } catch (e) {
      setResult({ inserted: 0, updated: 0, skipped: 0, errors: [{ row: 0, message: String(e) }] });
      setStep('result');
    } finally {
      setImporting(false);
    }
  };

  // ── Estatísticas da prévia ────────────────────────────────────────────────

  const validCount   = validated.filter((r) => r._errors.length === 0).length;
  const invalidCount = validated.filter((r) => r._errors.length > 0).length;
  const mappedFields = Object.values(mapping).filter((v) => v !== IGNORE_KEY);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 px-4 sm:px-6 py-6 max-w-screen-lg mx-auto w-full">

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/app/contatos')}
          className="text-ink-muted hover:text-ink transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-accent" />
            Importar Contatos
          </h1>
          <p className="text-sm text-ink-muted mt-0.5">CSV ou XLSX — até 10.000 linhas por importação.</p>
        </div>
      </div>

      {/* Stepper */}
      <StepIndicator current={step} />

      {/* ── Passo 1: Upload ── */}
      {step === 'upload' && (
        <div
          ref={dropRef}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`glass border-2 border-dashed rounded-2xl flex flex-col items-center justify-center gap-4 p-12 transition-colors ${
            dragOver ? 'border-accent/60 bg-accent/5' : 'border-hairline hover:border-accent/40'
          }`}
        >
          <div className="h-16 w-16 rounded-2xl bg-accent/10 flex items-center justify-center">
            <Upload className="h-8 w-8 text-accent" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-ink text-lg">Arraste o arquivo aqui</p>
            <p className="text-sm text-ink-muted mt-1">ou clique para selecionar</p>
            <p className="text-xs text-ink-subtle mt-2">Suporta <strong>.csv</strong>, <strong>.xlsx</strong> e <strong>.xls</strong></p>
          </div>
          <label className="btn-accent rounded-xl px-6 py-2.5 text-sm font-semibold cursor-pointer">
            Selecionar arquivo
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              className="sr-only"
              onChange={(e) => e.target.files?.[0] && parseFile(e.target.files[0])}
            />
          </label>
        </div>
      )}

      {/* ── Passo 2: Mapeamento ── */}
      {step === 'mapping' && (
        <div className="glass border border-hairline rounded-2xl overflow-hidden">
          <div className="px-6 py-4 hairline-b flex items-center justify-between">
            <div>
              <p className="font-semibold text-ink">{fileName}</p>
              <p className="text-xs text-ink-muted mt-0.5">{rawRows.length.toLocaleString('pt-BR')} linhas · {columns.length} colunas detectadas</p>
            </div>
            <span className="text-xs text-ink-subtle">{mappedFields.length} campo{mappedFields.length !== 1 ? 's' : ''} mapeado{mappedFields.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="divide-y divide-hairline max-h-[480px] overflow-y-auto">
            {columns.map((col) => (
              <MappingRow
                key={col}
                column={col}
                sample={rawRows[0]?.[col] ?? ''}
                value={mapping[col] ?? IGNORE_KEY}
                onChange={(v) => setMapping((m) => ({ ...m, [col]: v }))}
              />
            ))}
          </div>

          <div className="px-6 py-4 hairline-t flex justify-between items-center">
            <button
              onClick={() => setStep('upload')}
              className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" /> Trocar arquivo
            </button>
            <button
              onClick={goToPreview}
              disabled={!mappedFields.includes('full_name')}
              className="flex items-center gap-2 btn-accent rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer disabled:opacity-40"
            >
              Prévia e validação <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Passo 3: Prévia ── */}
      {step === 'preview' && (
        <div className="space-y-4">
          {/* Resumo de validação */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="glass border border-hairline rounded-2xl px-5 py-4 flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 shrink-0" />
              <div>
                <p className="text-2xl font-bold font-display text-ink">{validCount.toLocaleString('pt-BR')}</p>
                <p className="text-xs text-ink-muted">Prontos para importar</p>
              </div>
            </div>
            <div className={`glass border rounded-2xl px-5 py-4 flex items-center gap-3 ${invalidCount > 0 ? 'border-danger/30' : 'border-hairline'}`}>
              <XCircle className={`h-8 w-8 shrink-0 ${invalidCount > 0 ? 'text-danger' : 'text-ink-subtle'}`} />
              <div>
                <p className="text-2xl font-bold font-display text-ink">{invalidCount.toLocaleString('pt-BR')}</p>
                <p className="text-xs text-ink-muted">Com erros (ignorados)</p>
              </div>
            </div>
            <div className="glass border border-hairline rounded-2xl px-5 py-4 flex items-center gap-3">
              <Users className="h-8 w-8 text-accent shrink-0" />
              <div>
                <p className="text-2xl font-bold font-display text-ink">{validated.length.toLocaleString('pt-BR')}</p>
                <p className="text-xs text-ink-muted">Total de linhas</p>
              </div>
            </div>
          </div>

          {/* Aviso se há erros */}
          {invalidCount > 0 && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-400/5 border border-amber-400/20">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-ink">
                <strong>{invalidCount} linha{invalidCount !== 1 ? 's' : ''}</strong> com erro serão ignoradas.
                As demais <strong>{validCount}</strong> serão importadas normalmente.
              </p>
            </div>
          )}

          {/* Tabela de amostra (primeiras 10 linhas válidas) */}
          <div className="glass border border-hairline rounded-2xl overflow-hidden">
            <div className="px-5 py-3 hairline-b flex items-center gap-2">
              <Info className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-ink">Amostra — primeiras {Math.min(10, validCount)} linhas válidas</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="hairline-b">
                    {['Nome', 'Categoria', 'Telefone', 'E-mail', 'Cidade/UF', 'Bairro'].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-wider text-ink-subtle">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {validated
                    .filter((r) => r._errors.length === 0)
                    .slice(0, 10)
                    .map((r) => (
                      <tr key={r._rowIndex} className="hover:bg-white/3 transition-colors">
                        <td className="px-4 py-2.5 text-ink font-medium truncate max-w-[180px]">{r.full_name}</td>
                        <td className="px-4 py-2.5 text-ink-muted">{r.category}</td>
                        <td className="px-4 py-2.5 text-ink-muted font-mono">{r.phone ?? '—'}</td>
                        <td className="px-4 py-2.5 text-ink-muted truncate max-w-[160px]">{r.email ?? '—'}</td>
                        <td className="px-4 py-2.5 text-ink-muted">{[r.city, r.state_code].filter(Boolean).join(' / ') || '—'}</td>
                        <td className="px-4 py-2.5 text-ink-muted">{r.neighborhood ?? '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Erros detalhados (colapsável) */}
          {invalidCount > 0 && (
            <ErrorList rows={validated.filter((r) => r._errors.length > 0)} />
          )}

          <div className="flex justify-between items-center pt-2">
            <button
              onClick={() => setStep('mapping')}
              className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" /> Voltar ao mapeamento
            </button>
            <button
              onClick={runImport}
              disabled={validCount === 0 || importing}
              className="flex items-center gap-2 btn-accent rounded-xl px-6 py-2.5 text-sm font-semibold cursor-pointer disabled:opacity-40"
            >
              {importing
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Importando…</>
                : <><CheckCircle2 className="h-4 w-4" /> Importar {validCount.toLocaleString('pt-BR')} contato{validCount !== 1 ? 's' : ''}</>
              }
            </button>
          </div>
        </div>
      )}

      {/* ── Passo 4: Resultado ── */}
      {step === 'result' && result && (
        <div className="space-y-6">
          {/* Cards de resultado */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ResultCard icon={CheckCircle2} color="text-emerald-400" label="Novos inseridos"  value={result.inserted} />
            <ResultCard icon={RefreshCw}   color="text-accent"      label="Atualizados"       value={result.updated}  />
            <ResultCard icon={XCircle}     color="text-danger"      label="Erros na importação" value={result.errors.length} />
            <ResultCard icon={Users}       color="text-ink-muted"   label="Total processados"  value={result.inserted + result.updated} />
          </div>

          {/* Erros da importação (da Edge Function) */}
          {result.errors.length > 0 && (
            <div className="glass border border-danger/20 rounded-2xl p-5 space-y-2">
              <p className="font-semibold text-sm text-danger flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Erros durante importação
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i} className="text-xs text-ink-muted font-mono">
                    Linha {e.row}: {e.message}
                  </li>
                ))}
                {result.errors.length > 20 && (
                  <li className="text-xs text-ink-subtle">…e mais {result.errors.length - 20} erros</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/app/contatos')}
              className="flex items-center gap-2 btn-accent rounded-xl px-6 py-2.5 text-sm font-semibold cursor-pointer"
            >
              <Users className="h-4 w-4" /> Ver contatos
            </button>
            <button
              onClick={() => {
                setStep('upload');
                setRawRows([]); setColumns([]); setMapping({});
                setValidated([]); setResult(null); setFileName('');
              }}
              className="flex items-center gap-2 btn-ghost rounded-xl px-5 py-2.5 text-sm font-semibold cursor-pointer"
            >
              <Upload className="h-4 w-4" /> Nova importação
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-componentes ────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'upload',  label: 'Upload'    },
    { key: 'mapping', label: 'Mapeamento'},
    { key: 'preview', label: 'Prévia'    },
    { key: 'result',  label: 'Resultado' },
  ];
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center gap-0">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center flex-1 last:flex-none">
          <div className="flex items-center gap-2">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
              i < idx  ? 'bg-accent text-midnight'
              : i === idx ? 'bg-accent/20 text-accent border border-accent/50'
              : 'bg-white/5 text-ink-subtle border border-hairline'
            }`}>
              {i < idx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i === idx ? 'text-accent' : i < idx ? 'text-ink-muted' : 'text-ink-subtle'}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-px mx-3 ${i < idx ? 'bg-accent/40' : 'bg-hairline'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function MappingRow({
  column, sample, value, onChange,
}: {
  column: string; sample: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-5 py-3">
      {/* Coluna do arquivo */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink truncate">{column}</p>
        {sample && (
          <p className="text-[11px] text-ink-subtle truncate mt-0.5 font-mono">{sample}</p>
        )}
      </div>

      {/* Seta */}
      <ArrowRight className="h-4 w-4 text-ink-subtle shrink-0" />

      {/* Selector de campo */}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-lg bg-midnight-2/60 border border-hairline
                     px-3 pr-7 py-2 text-sm text-ink focus:outline-none focus:border-accent/50
                     transition-all [&_option]:bg-midnight-3 cursor-pointer"
        >
          <option value={IGNORE_KEY}>— Ignorar —</option>
          {SYSTEM_FIELDS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}{f.required ? ' *' : ''}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-subtle" />
      </div>
    </div>
  );
}

function ErrorList({ rows }: { rows: ValidatedRow[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass border border-danger/20 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 flex items-center justify-between text-sm font-semibold text-danger cursor-pointer hover:bg-danger/5 transition-colors"
      >
        <span className="flex items-center gap-2">
          <XCircle className="h-4 w-4" />
          {rows.length} linha{rows.length !== 1 ? 's' : ''} com erro
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="divide-y divide-hairline max-h-48 overflow-y-auto">
          {rows.slice(0, 50).map((r) => (
            <div key={r._rowIndex} className="px-5 py-2.5 flex items-start gap-3">
              <span className="font-mono text-[10px] text-ink-subtle shrink-0 mt-0.5">L{r._rowIndex}</span>
              <div>
                <span className="text-xs text-ink">{r.full_name || '(sem nome)'}</span>
                <p className="text-[11px] text-danger mt-0.5">{r._errors.join(' · ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultCard({
  icon: Icon, color, label, value,
}: {
  icon: React.ElementType; color: string; label: string; value: number;
}) {
  return (
    <div className="glass border border-hairline rounded-2xl px-5 py-4 flex items-center gap-3">
      <Icon className={`h-8 w-8 shrink-0 ${color}`} />
      <div>
        <p className="text-2xl font-bold font-display text-ink">{value.toLocaleString('pt-BR')}</p>
        <p className="text-xs text-ink-muted">{label}</p>
      </div>
    </div>
  );
}
